import { Section } from '@factorialco/deprecated-design-system'
import {
  ProjectDetailsPageSettingsPageFinancialDetailsSectionFragment,
  ProjectManagementFinancialDetailsFormQuery,
  ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectDocument,
  ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectMutation,
  ProjectManagementProjectDetailsPageSettingsPageQuery,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import ProjectFinancialDetailsForm, {
  IProjectFinancialDetailsValues,
} from 'modules/project_management/components/forms/ProjectFinancialDetailsForm'
import { financialDetailsAttributes } from 'modules/project_management/lib/constants'

export const FinancialDetailsSection: React.FC<{
  data:
    | ProjectManagementFinancialDetailsFormQuery
    | ProjectManagementProjectDetailsPageSettingsPageQuery
  project: ProjectDetailsPageSettingsPageFinancialDetailsSectionFragment
}> = ({ data, project }) => {
  const [updateFinancialDetails] = useMutation(
    ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectMutation
      ) => data.projectManagementMutations.updateFinancialDetailsProjectmanagementProject,
    }
  )

  const { values, errors, setValues, handleSubmit, dirty } =
    useForm<IProjectFinancialDetailsValues>({
      defaultValues: {
        fixedCost: project.fixedCostCents ? (project.fixedCostCents / 100).toString() : undefined,
        legalEntityId: project.legalEntity.id.toString(),
        isBillable: project.isBillable,
      },
      enableReinitialize: true,
      validate: (data) => {
        const errorValidation: FormErrors<IProjectFinancialDetailsValues> = {}

        if (!data.fixedCost) return
        if (isNaN(Number(data.fixedCost.replace(',', '.')))) {
          errorValidation.fixedCost = i18n.t(
            'project_management.forms.financial_details.fixed_costs.format_error'
          )
        }
        if (Number(data.fixedCost) < 0) {
          errorValidation.fixedCost = i18n.t(
            'project_management.forms.financial_details.fixed_costs.negative_error'
          )
        }

        return errorValidation
      },
      onSubmit: async (data, { setErrors }) => {
        const result = await updateFinancialDetails({
          variables: {
            projectId: project.id,
            fixedCostCents: data.fixedCost ? Number(data.fixedCost) * 100 : undefined,
            legalEntityId: Number(data.legalEntityId),
            isBillable: data.isBillable,
          },
        })

        const handled = handleResult(result, (err) => {
          if (err) setErrors(errors)

          if (handled.success) {
            track(
              trackingEvents.projectManagement.settings.projectFinancialDetailsProjectSettings,
              {
                billable: data.isBillable,
                fixedCost: !!data.fixedCost,
                legalEntityId: data.legalEntityId,
              }
            )
          }
        })

        return handled
      },
    })

  const onChangeValue = (
    fieldName: financialDetailsAttributes,
    fieldValue: boolean | string | undefined
  ) => {
    if (fieldValue === '') {
      fieldValue = undefined
    }
    setValues({ ...values, [fieldName]: fieldValue })
  }

  return (
    <Section.Form
      illustration="money-bag"
      title={i18n.t('project_management.settings.sections.financial_details.title')}
      description={i18n.t('project_management.settings.sections.financial_details.description')}
      onSubmit={handleSubmit}
      submitLabel={i18n.t('common.submit')}
      isDirty={dirty}
      paddingX="s0"
    >
      <ProjectFinancialDetailsForm
        onChangeValue={onChangeValue}
        data={data}
        values={values}
        errors={errors}
        isOwner={!!project._policies?.assignOwner.can}
      />
    </Section.Form>
  )
}
